import { useState } from 'react'
import { Section } from '@/components/ds/Section'
import { Button } from '@/components/ui/Button'
import { DialogClose, DialogContent, DialogRoot } from '@/components/ui/Dialog'
import type { RecoveryBatch, RecoveryBatchAction } from '@/api/endpoints/channelRecovery'
import { batchProgressLabel, canResume, canUndo, isBatchActive } from '@/features/recovery/labels'
import styles from '@/features/recovery/pages/RecoveryPage/RecoveryPage.module.scss'

const ITEM_STATUS_LABEL: Record<string, string> = {
  pending: 'pendentes',
  in_flight: 'em curso',
  done: 'feitos',
  failed: 'falharam',
  undone: 'desfeitos',
  undo_skipped: 'mexidos à mão',
  skipped: 'pulados',
}

export interface BatchesSectionProps {
  batches: RecoveryBatch[]
  enabled: boolean | undefined
  busy: boolean
  onAction: (batchId: number, action: RecoveryBatchAction) => void
}

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })
}

export function BatchesSection({ batches, enabled, busy, onAction }: BatchesSectionProps) {
  const [undoId, setUndoId] = useState<number | null>(null)
  const undoBatch = batches.find((b) => b.id === undoId)

  return (
    <Section
      title="Lotes"
      description="Cada lote anda até o orçamento de cota do dia e segue sozinho no dia seguinte."
    >
      {batches.length === 0 ? (
        <p className={styles.muted}>Nenhum lote ainda.</p>
      ) : (
        <ul className={styles.batchList}>
          {batches.map((b) => (
            <li key={b.id} className={styles.batch}>
              <div className={styles.batchHead}>
                <strong>Lote #{b.id}</strong>
                <span>{batchProgressLabel(b)}</span>
                <span className={styles.muted}>
                  {b.origin === 'manual_import' ? 'importado · ' : ''}
                  {b.target_privacy ?? ''} · {formatDate(b.started_at ?? b.created_at)}
                </span>
              </div>
              <div className={styles.muted}>
                {Object.entries(b.counts)
                  .map(
                    ([status, count]) => `${String(count)} ${ITEM_STATUS_LABEL[status] ?? status}`,
                  )
                  .join(' · ')}
              </div>
              {b.status === 'failed' && b.error ? (
                <div className={styles.error}>{b.error}</div>
              ) : null}
              <div className={styles.actionsRow}>
                {isBatchActive(b) ? (
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={busy}
                    onClick={() => {
                      onAction(b.id, 'cancel')
                    }}
                  >
                    Pausar
                  </Button>
                ) : null}
                {canResume(b) ? (
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={busy || enabled === false}
                    onClick={() => {
                      onAction(b.id, 'resume')
                    }}
                  >
                    Retomar
                  </Button>
                ) : null}
                {canUndo(b) ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={busy || enabled === false}
                    onClick={() => {
                      setUndoId(b.id)
                    }}
                  >
                    Desfazer lote
                  </Button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}

      <DialogRoot
        open={undoBatch !== undefined}
        onOpenChange={(open) => {
          if (!open) setUndoId(null)
        }}
      >
        {undoBatch ? (
          <DialogContent
            title={`Desfazer o lote #${String(undoBatch.id)}?`}
            description="Os vídeos trocados voltam à visibilidade de antes, ao vivo no YouTube. Quem você mexeu à mão depois fica como está."
            footer={
              <>
                <DialogClose asChild>
                  <Button variant="ghost">Cancelar</Button>
                </DialogClose>
                <Button
                  variant="danger"
                  onClick={() => {
                    onAction(undoBatch.id, 'undo')
                    setUndoId(null)
                  }}
                >
                  Desfazer lote
                </Button>
              </>
            }
          >
            <p className={styles.muted}>{batchProgressLabel(undoBatch)}</p>
          </DialogContent>
        ) : null}
      </DialogRoot>
    </Section>
  )
}
